<template>
  <div id="customize-dashboard" class="uk-modal-container" uk-modal>
    <div class="uk-modal-dialog uk-modal-body dashboard-modal">
      <div class="dashboard-modal__content">
        <h2 class="dashboard-modal__title">{{ $t("general.customize") }}</h2>

        <section
          class="dashboard-modal__section"
          aria-labelledby="active-widgets-heading"
        >
          <header class="dashboard-modal__section-header">
            <h3
              id="active-widgets-heading"
              class="dashboard-modal__section-title"
            >
              <i class="uil-eye" aria-hidden="true"></i>
              {{ $t("survey.active") }}
            </h3>
            <button
              :aria-label="$t('menu.company_dashboard.reset_menu')"
              :disabled="!hasWidgetData"
              :uk-tooltip="
                'title:' +
                $t('menu.company_dashboard.reset_menu') +
                '; pos: top-center'
              "
              class="dashboard-modal__icon-button"
              type="button"
              @click="$emit('reset')"
            >
              <i class="uil-refresh" aria-hidden="true"></i>
            </button>
          </header>

          <draggable
            v-if="hasWidgetData"
            :animation="120"
            :delay="160"
            :delay-on-touch-only="true"
            :list="widgets.data.active"
            :move="checkMinMenuLimit"
            :touch-start-threshold="4"
            class="dashboard-widget-grid"
            group="widget"
          >
            <div
              v-for="element in widgets.data.active"
              :key="element.id"
              class="dashboard-widget-grid__item"
            >
              <div class="dashboard-widget-card">
                <i :class="element.icon" aria-hidden="true"></i>
                <i
                  class="uil-align-justify dashboard-widget-card__drag"
                  aria-hidden="true"
                ></i>
                <span>{{ $t(element.key_name) }}</span>
              </div>
            </div>
          </draggable>
        </section>

        <section
          class="dashboard-modal__section"
          aria-labelledby="passive-widgets-heading"
        >
          <h3
            id="passive-widgets-heading"
            class="dashboard-modal__section-title"
          >
            <i class="uil-eye-slash" aria-hidden="true"></i>
            {{ $t("survey.inactive") }}
          </h3>

          <draggable
            v-if="hasWidgetData"
            :animation="120"
            :delay="160"
            :delay-on-touch-only="true"
            :list="widgets.data.passive"
            :move="checkMaxMenuLimit"
            :touch-start-threshold="4"
            class="dashboard-widget-grid"
            group="widget"
          >
            <div
              v-for="element in widgets.data.passive"
              :key="element.id"
              class="dashboard-widget-grid__item"
            >
              <div class="dashboard-widget-card">
                <i :class="element.icon" aria-hidden="true"></i>
                <i
                  class="uil-align-justify dashboard-widget-card__drag"
                  aria-hidden="true"
                ></i>
                <span>{{ $t(element.key_name) }}</span>
              </div>
            </div>
          </draggable>
        </section>
      </div>

      <footer class="dashboard-modal__actions">
        <button class="dashboard-modal__button uk-modal-close" type="button">
          {{ $t("general.close") }}
        </button>
        <button
          :disabled="!hasWidgetData"
          class="dashboard-modal__button dashboard-modal__button--primary uk-modal-close"
          type="button"
          @click="$emit('save')"
        >
          {{ $t("general.save") }}
        </button>
      </footer>
    </div>
  </div>
</template>

<script>
import draggable from "vuedraggable";

export default {
  name: "DashboardCustomizeModal",
  components: { draggable },
  props: {
    maxMenuItems: {
      type: Number,
      required: true,
    },
    minMenuItems: {
      type: Number,
      required: true,
    },
    widgets: {
      type: Object,
      required: true,
    },
  },
  computed: {
    hasWidgetData() {
      return Boolean(this.widgets && this.widgets.data);
    },
  },
  methods: {
    checkMaxMenuLimit(evt) {
      if (
        evt.from !== evt.to &&
        this.widgets.data.active.length >= this.maxMenuItems
      ) {
        return false;
      }
    },
    checkMinMenuLimit(evt) {
      if (
        evt.from !== evt.to &&
        this.widgets.data.active.length <= this.minMenuItems
      ) {
        return false;
      }
    },
  },
};
</script>

<style scoped>
.dashboard-modal {
  border: 1px solid #e5e7eb;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  max-width: 960px !important;
  overflow: hidden;
  padding: 0 !important;
}

.dashboard-modal__content {
  padding: 24px;
}

.dashboard-modal__title {
  color: #1f2937;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.4;
  margin: 0 0 24px;
}


.dashboard-modal__section-header {
  align-items: center;
  border-bottom: 1px solid #d1d5db;
  display: flex;
  justify-content: space-between;
}

.dashboard-modal__section-title {
  align-items: center;
  color: #1f2937;
  display: flex;
  font-size: 20px;
  font-weight: 600;
  gap: 8px;
  line-height: 1.4;
  margin: 0;
  padding-bottom: 8px;
}

.dashboard-modal__section > .dashboard-modal__section-title {
  border-bottom: 1px solid #d1d5db;
  display: flex;
}

.dashboard-modal__icon-button {
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: 10px;
  color: #374151;
  display: inline-flex;
  font-size: 20px;
  justify-content: center;
  min-height: 48px;
  min-width: 48px;
  touch-action: manipulation;
}

.dashboard-modal__icon-button:active {
  opacity: 0.85;
  transform: scale(0.98);
}

.dashboard-modal__icon-button:disabled,
.dashboard-modal__button:disabled {
  opacity: 0.5;
  pointer-events: none;
}

.dashboard-modal__icon-button:focus-visible,
.dashboard-modal__button:focus-visible {
  outline: 3px solid #2563eb;
  outline-offset: 2px;
}

.dashboard-widget-grid {
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin-top: 16px;
}

.dashboard-widget-card {
  align-items: center;
  background: #f8fafc;
  border: 1px solid #dbe2ea;
  border-radius: 14px;
  color: #1f2937;
  display: flex;
  flex-direction: column;
  font-size: 16px;
  font-weight: 600;
  gap: 4px;
  justify-content: center;
  min-height: 100px;
  overflow-wrap: anywhere;
  padding: 12px 8px;
  position: relative;
  text-align: center;
  touch-action: manipulation;
  user-select: none;
  -webkit-user-select: none;
}

.dashboard-widget-card > i:first-child {
  color: #2563eb;
  font-size: 26px;
  line-height: 1;
}

.dashboard-widget-card__drag {
  align-items: center;
  color: #6b7280;
  display: flex;
  font-size: 16px;
  height: 36px;
  justify-content: center;
  pointer-events: none;
  position: absolute;
  right: 0;
  top: 0;
  width: 36px;
}

.dashboard-widget-card span {
  line-height: 1.4;
}

.dashboard-modal__actions {
  background: #fff;
  border-top: 1px solid #e5e7eb;
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  padding: 16px 24px 24px;
}

.dashboard-modal__button {
  background: #fff;
  border: 1px solid #9ca3af;
  border-radius: 10px;
  color: #374151;
  font-size: 16px;
  font-weight: 600;
  min-height: 48px;
  padding: 8px 16px;
  touch-action: manipulation;
}

.dashboard-modal__button--primary {
  background: #1d4ed8;
  border-color: #1d4ed8;
  color: #fff;
}

.dashboard-modal__button:active {
  opacity: 0.85;
  transform: scale(0.98);
}

@media (hover: hover) and (pointer: fine) {
  .dashboard-modal__button:not(:disabled),
  .dashboard-modal__icon-button:not(:disabled) {
    cursor: pointer;
  }

  .dashboard-widget-grid__item {
    cursor: grab;
  }

  .dashboard-widget-card:hover {
    background: #fff;
    border-color: #cbd5e1;
  }

  .dashboard-widget-grid__item.sortable-chosen,
  .dashboard-widget-grid__item.sortable-drag {
    cursor: grabbing;
  }
}

@media (min-width: 640px) {
  .dashboard-widget-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (min-width: 960px) {
  .dashboard-widget-grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

@media (max-width: 767px) {
  #customize-dashboard.uk-open {
    align-items: flex-end;
    display: flex !important;
    justify-content: center;
    padding: 0;
  }

  .dashboard-modal {
    -webkit-overflow-scrolling: touch;
    border-radius: 16px 16px 0 0;
    box-sizing: border-box;
    display: flex !important;
    flex-direction: column;
    height: auto;
    margin: 0;
    max-height: calc(100vh - 16px);
    max-height: calc(100dvh - 16px);
    max-width: none !important;
    overflow: hidden;
    padding: 0 !important;
    width: 100% !important;
  }

  .dashboard-modal__content {
    flex: 1 1 auto;
    min-height: 0;
    overflow-x: hidden;
    overflow-y: auto;
    overscroll-behavior: contain;
    padding: 24px 16px;
    padding-bottom: 24px;
    -webkit-overflow-scrolling: touch;
  }

  .dashboard-modal__actions {
    flex: 0 0 auto;
    padding: 16px;
    padding-bottom: calc(16px + env(safe-area-inset-bottom));
  }

  .dashboard-widget-card {
    min-height: 88px;
  }

  .dashboard-widget-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (prefers-reduced-motion: reduce) {
  .dashboard-modal__button:active,
  .dashboard-modal__icon-button:active {
    transform: none;
  }
}
</style>
